import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const AUTO_LOGOUT_MINUTES = 20;
const CHECK_MS = 30 * 1000;
const WRITE_EVERY_MS = 5 * 1000;
// Shared by every open window of the app (the main window, AI Office, PDF tabs): activity in any of them
// keeps all of them logged in, and a logout in one sends the others to the login page too.
const ACTIVITY_KEY = "lastActivity";
const LOGGED_OUT_KEY = "loggedOut";

// Clears the login data on logout, but keeps the AI Office preferences (aio-…: colours, voice, office life).
export function clearSession() {
  try {
    const keep = Object.keys(localStorage)
      .filter((k) => k.startsWith("aio-") || k.startsWith("aio."))
      .map((k) => [k, localStorage.getItem(k)]);
    localStorage.clear();
    keep.forEach(([k, v]) => localStorage.setItem(k, v));
    localStorage.setItem(LOGGED_OUT_KEY, String(Date.now())); // tells the other windows
  } catch (_) { /* storage blocked */ }
}

function AutoLogoutWrapper({ children }) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const lastOwn = useRef(Date.now());
  const lastWrite = useRef(0);
  const loggingOut = useRef(false);

  useEffect(() => {
    const markActive = () => {
      const t = Date.now();
      lastOwn.current = t;
      if (t - lastWrite.current < WRITE_EVERY_MS) return;
      lastWrite.current = t;
      try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch (_) { /* storage blocked */ }
    };
    const lastActive = () => {
      let shared = 0;
      try { shared = Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch (_) { /* storage blocked */ }
      return Math.max(lastOwn.current, shared);
    };
    const check = () => {
      if (loggingOut.current) return;
      if (Date.now() - lastActive() < AUTO_LOGOUT_MINUTES * 60 * 1000) return;
      loggingOut.current = true;
      axios.post(`${window.backendUrl}/logout`, {}, { withCredentials: true })
        .finally(() => {
          clearSession();
          loggingOut.current = false;
          lastOwn.current = Date.now();
          navigateRef.current('/');
        });
    };
    // Another window logged out (or found the login expired and cleared it): go to the login page here too.
    const onStorage = (e) => {
      if ((e.key === LOGGED_OUT_KEY && e.newValue) || e.key === null) {
        lastOwn.current = Date.now();
        if (window.location.pathname !== '/') navigateRef.current('/');
      }
    };

    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    events.forEach((event) => window.addEventListener(event, markActive));
    window.addEventListener("storage", onStorage);
    lastWrite.current = 0;
    markActive();
    const timer = setInterval(check, CHECK_MS);

    return () => {
      events.forEach((event) => window.removeEventListener(event, markActive));
      window.removeEventListener("storage", onStorage);
      clearInterval(timer);
    };
  }, []);

  return <>{children}</>;
}

export default AutoLogoutWrapper;
